import { useCallback, useEffect, useMemo } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { CircleHelpIcon, InfoIcon } from 'lucide-react';
import { Resolver, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { toast } from 'sonner';

import { CharacterCounter } from '@/components/atoms/CharacterCounter/CharacterCounter';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import {
  FormAutocomplete,
  AutocompleteOption,
} from '@/components/ui/custom/Autocomplete/Autocomplete';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import useDialogAlert from '@/hooks/useDialogAlert';
import useDialogs from '@/hooks/useDialogs';
import useFocusOnInput from '@/hooks/useFocusOnInput';
import useWhatsAppActive from '@/hooks/useWhatsAppActive';
import {
  selectHasOtherAutoOpenWebview,
  selectNodeByActionId,
  selectNodeIdByActionId,
  selectSelectedAction,
} from '@/redux/dialogs/selectors';
import { updateAction } from '@/redux/nodes/actions';
import { selectBrainId } from '@/redux/session/selectors';
import { Action } from '@/shared/types/action';
import { RootState } from '@/shared/types/state';
import { getFormErrorsKey } from '@/util/util';
import { LENGTH_S, WHATSAPP_BUTTON_TEXT_LIMIT } from '@/util/validation/constants';
import { webviewActionSchema } from '@/util/validation/schemas/action-schemas';
import { getWhatsappButtonLabelLength } from '@/util/validation/schemas/whatsapp-schemas';

import ToolkitWrapper from '../../ToolkitWrapper';

type WebviewAction = Extract<Action, { type: 'webview' }>;

type WebviewForm = {
  url: string;
  name: string;
  label: string;
  trigger_node_id: string;
  height?: string;
  auto_open?: boolean;
};

const getFormDefaults = (webview: Omit<WebviewAction, 'action_id'>): WebviewForm => ({
  url: webview.url,
  name: webview.name,
  label: webview.label,
  trigger_node_id: webview.trigger_node_id ?? '',
  height: webview.height,
  auto_open: webview.auto_open ?? false,
});

const ToolkitActionWebview = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { dialogId } = useParams();

  const heightOptions = useMemo(
    () => [
      { value: 'tall', label: t('dialog.webview.tall') },
      { value: 'compact', label: t('dialog.webview.compact') },
      { value: 'full', label: t('dialog.webview.full') },
      { value: 'new_window', label: t('dialog.webview.new_window') },
    ],
    [t]
  );

  const { actionId, brainId, node_id, ...webview } = useSelector((state: RootState) => {
    const selectedAction = selectSelectedAction(state) as WebviewAction;
    const { action_id, ...rest } = selectedAction;
    return {
      actionId: action_id,
      brainId: selectBrainId(state),
      node_id: selectNodeByActionId(state as RootState, action_id).node_id,
      ...rest,
    };
  }, shallowEqual);
  const parentNodeId = useSelector(selectNodeIdByActionId(actionId));
  const { detailedOptions } = useDialogs(brainId, dialogId, node_id);
  const whatsAppActive = useWhatsAppActive();

  const comboboxOptions: AutocompleteOption[] = useMemo(() => {
    return detailedOptions.map((option) => ({
      value: option.value,
      label: option.label,
      category: option.dialog_name,
      disabled: option.value === node_id,
    }));
  }, [detailedOptions, node_id]);

  const form = useForm<WebviewForm>({
    mode: 'onChange',
    defaultValues: getFormDefaults(webview),
    resolver: yupResolver(webviewActionSchema) as Resolver<WebviewForm>,
  });
  const {
    control,
    reset,
    trigger,
    setFocus,
    setValue,
    formState: { errors },
  } = form;
  const watchAutoOpen = useWatch({ control, name: 'auto_open' });
  const watchLabel = useWatch({ control, name: 'label' }) ?? '';

  // Reset form when switching to a different action
  useEffect(() => {
    reset(getFormDefaults(webview));
  }, [actionId, reset]);

  const handleFieldChange = useCallback(
    (key: keyof WebviewForm, value: string | boolean | undefined) => {
      dispatch(updateAction({ actionId, action: { [key]: value } }));
    },
    [actionId, dispatch]
  );

  const hasOtherAutoOpen = useSelector((state: RootState) =>
    selectHasOtherAutoOpenWebview(state, actionId)
  );

  const handleAutoOpenChange = useCallback(
    (checked: boolean) => {
      if (checked && hasOtherAutoOpen) {
        toast.warning(t('dialog.webview.auto_open_warning'));
      }
      setValue('auto_open', checked);
      handleFieldChange('auto_open', checked);
    },
    [handleFieldChange, hasOtherAutoOpen, setValue, t]
  );

  const { setFieldError } = useDialogAlert({
    id: actionId,
    nodeId: parentNodeId,
    title: t('actions.types.webview'),
    type: webview.type,
  });

  useEffect(() => {
    const fields = ['url', 'name', 'label', 'trigger_node_id'] as const;

    for (const field of fields) {
      setFieldError(field, errors[field]?.message);
    }
  }, [getFormErrorsKey(errors), setFieldError]);

  useEffect(() => {
    trigger();
  }, [trigger]);

  useFocusOnInput('url', errors, setFocus);

  return (
    <ToolkitWrapper type="webview">
      <Form {...form}>
        <div className="flex flex-col gap-4 pb-6">
          <FormField
            control={control}
            name="url"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('dialog.webview.webview_url')}</FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    minRows={1}
                    maxRows={4}
                    className="resize-none break-all"
                    placeholder={t('dialog.url_placeholder')}
                    onChange={(event) => {
                      field.onChange(event);
                      handleFieldChange('url', event.target.value);
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-1">
                  {t('common.title')}
                  <InfoTooltip content={t('dialog.webview.title_tooltip')} side="right">
                    <CircleHelpIcon className="size-3.5 text-foreground-muted" />
                  </InfoTooltip>
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder={t('dialog.webview.type_name')}
                    onChange={(event) => {
                      field.onChange(event);
                      handleFieldChange('name', event.target.value);
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="label"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-1">
                  {t('dialog.webview.label')}
                  {whatsAppActive && (
                    <InfoTooltip content={t('dialog.webview.label_tooltip')} side="right">
                      <CircleHelpIcon className="size-3.5 text-foreground-muted" />
                    </InfoTooltip>
                  )}
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder={t('dialog.webview.label_placeholder')}
                    onChange={(event) => {
                      field.onChange(event);
                      handleFieldChange('label', event.target.value);
                    }}
                  />
                </FormControl>
                <FormMessage />
                {whatsAppActive && (
                  <div className="flex justify-end">
                    <CharacterCounter
                      count={getWhatsappButtonLabelLength(watchLabel)}
                      max={LENGTH_S}
                      warningAt={WHATSAPP_BUTTON_TEXT_LIMIT}
                    />
                  </div>
                )}
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="trigger_node_id"
            render={() => (
              <FormItem>
                <FormLabel className="flex items-center gap-1">
                  {t('dialog.trigger_node')}
                  <InfoTooltip content={t('dialog.webview.trigger_node_tooltip')} side="right">
                    <CircleHelpIcon className="size-3.5 text-foreground-muted" />
                  </InfoTooltip>
                </FormLabel>
                <FormAutocomplete
                  control={control}
                  options={comboboxOptions}
                  name="trigger_node_id"
                  placeholder={t('dialog.jump_to.placeholder')}
                  onChange={(value) => handleFieldChange('trigger_node_id', value || undefined)}
                />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="height"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-1">
                  {t('dialog.webview.format')}
                  <InfoTooltip content={t('dialog.webview.format_tooltip')} side="right">
                    <CircleHelpIcon className="size-3.5 text-foreground-muted" />
                  </InfoTooltip>
                </FormLabel>
                <Select
                  value={field.value}
                  onValueChange={(value) => {
                    field.onChange(value);
                    handleFieldChange('height', value);
                  }}
                >
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {heightOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex items-start gap-2 align-middle">
            <Checkbox
              id="auto_open"
              checked={!!watchAutoOpen}
              onCheckedChange={handleAutoOpenChange}
              className="mt-0.5"
            />
            <div className="flex flex-col gap-1">
              <Label htmlFor="auto_open" className="cursor-pointer text-sm font-medium">
                {t('dialog.webview.auto_open_label')}
              </Label>
              <p className="text-sm text-foreground-muted">
                {t('dialog.webview.auto_open_description')}
              </p>
            </div>
          </div>

          <Alert variant="info" className="gap-y-2">
            <InfoIcon size={16} />
            <AlertTitle className="text-sm leading-5 font-semibold tracking-normal">
              {t('dialog.webview.banner.title')}
            </AlertTitle>
            <AlertDescription>{t('dialog.webview.banner.text')}</AlertDescription>
          </Alert>
        </div>
      </Form>
    </ToolkitWrapper>
  );
};

export default ToolkitActionWebview;
